import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import BasicDialog from "../../../components/base/BasicDialog";
import { AgreementDialogTabType, AgreementDialogTabs } from "./AgreementDialogTabs";

import LoadingButton from "@mui/lab/LoadingButton";
import AddEditDocsRestrictedDialog from "../../../components/dialog/AddEditDocsRestrictedDialog";
import { SendEmailDialog } from "../../../components/dialog/EmailSender";
import { errorToast, successToast } from "../../../components/toast";
import { useAuth } from "../../../context/auth";
import { DOCS_RESTRICTED_ROLE, DOCS_RESTRICTED_USER } from "../../../helpers/constants";
import { useDialogBetter } from "../../../hooks/useDialogBetter";
import {
    AGREEMENT_KINDS,
    AGREEMENT_TYPES,
    AgreementKindEnum,
    DEFAULT_AGREEMENT_KIND,
} from "../../../schemas/agreements";
import {
    getCustomers,
    useCreateAgreement,
    useUpdateAgreement,
} from "../../../services/agreementsService";
import DateService from "../../../services/dateService";
import { useSendEmail } from "../../../services/mailSenderService";
import { normalizePercentageForSubmission } from "../../../utils";
import { UpdateNoteDialog } from "./Tabs/components/UpdateNoteDialog";
import { useIsAgreementFormDataValid } from "./useIsAgreementFormDataValid";
import useSellerId from "../../../hooks/useSellerId";
import { BankingSchema } from "../../../schemas/agreements";
import { formatDecimalNumber } from "../../../utils";

export const AgreementConfigurationDialogMode = ((AgreementConfigurationDialogMode) => {
    AgreementConfigurationDialogMode.CREATE = "CREATE";
    AgreementConfigurationDialogMode.EDIT = "EDIT";
    AgreementConfigurationDialogMode.ANNEX = "ANNEX";
    AgreementConfigurationDialogMode.PROLONGATE = "PROLONGATE";
    AgreementConfigurationDialogMode.TRANSFORM = "TRANSFORM";
    return AgreementConfigurationDialogMode;
})({});

export const AgreementConfigurationDialog = (props) => {
    const {
        onClose,
        mode = AgreementConfigurationDialogMode.CREATE,
        agreement,
        initialCustomerIds,
        initialProductIds,
        initialProductDiscounts,
        parentAgreementId,
        parentAgreementKind,
        sourceBookingId,
    } = props;

    const currentSellerId = useSellerId();

    const { t } = useTranslation();
    const { user } = useAuth();
    const { openDialog, closeDialog } = useDialogBetter();
    

    const [selectedTab, setSelectedTab] = useState(AgreementDialogTabType.AGREEMENT_TYPE);

    const getDefaultValues = () => {
        if (agreement != null) {
            return formDataFromAgreement(agreement, mode);
        }

        if (initialCustomerIds || initialProductIds) {
            const productDiscounts = {};
            if (initialProductIds) {
                for (const productId of initialProductIds) {
                    productDiscounts[productId] = initialProductDiscounts?.[productId] ?? 0;
                }
            }

            return {
                ...emptyFormData,
                customerIds: initialCustomerIds || [],
                productDiscounts,
                caretakerId: currentSellerId
            };
        }

        return {...emptyFormData, caretakerId: currentSellerId}
    };

    const form = useForm({
        resolver: zodResolver(createAgreementFormDataSchema),
        defaultValues: getDefaultValues(),
    });

    const { mutate: createAgreement, isPending: isCreatePending } = useCreateAgreement({
        onSuccess: () => {
            onClose();
            successToast(t("agreements.AgreementConfigurationDialog.agreementCreated"));
        },
        onError: () => {
            errorToast(t("agreements.AgreementConfigurationDialog.agreementCreationFailed"));
        },
    });

    const {
        mutate: updateAgreement,
        mutateAsync: updateAgreementAsync,
        isPending: isUpdatePending,
    } = useUpdateAgreement({
        onSuccess: () => {
            onClose();
            successToast(t("agreements.AgreementConfigurationDialog.agreementEdited"));
        },
        onError: () => {
            errorToast(t("agreements.AgreementConfigurationDialog.agreementEditFailed"));
        },
    });

    const { mutateAsync: sendEmail, isPending: isSendEmailPending } = useSendEmail({
        onSuccess: () => {
            successToast(t("agreements.AgreementConfigurationDialog.emailNotificationSent"));
        },
        onError: () => {
            errorToast(t("agreements.AgreementConfigurationDialog.emailNotificationFailed"));
        },
    });

    const isPending = isCreatePending || isUpdatePending || isSendEmailPending;

    const handleSubmitCreateMode = useCallback(() => {
        const formData = form.getValues();
        const requestBody = {
            ...createRequestBodyFromFormData(formData),
            ...(sourceBookingId && { source_booking: sourceBookingId }),
        };
        createAgreement(requestBody);
    }, [form, createAgreement, sourceBookingId]);

    const handleSubmitEditMode = useCallback(() => {
        const formData = form.getValues();
        const requestBody = createRequestBodyFromFormData(formData);
        const onUpdate = (note) => {
            const editRequestBody = { ...requestBody, update_note: note };
            updateAgreement({ agreementId: agreement.id, data: editRequestBody });
        };
        openDialog({
            Component: UpdateNoteDialog,
            componentProps: { onUpdate },
        });
    }, [form, updateAgreement, agreement, openDialog]);

    const handleSubmitProlongateMode = useCallback(() => {
        const formData = form.getValues();
        const requestBody = {
            ...createRequestBodyFromFormData(formData),
            agreement_type: AGREEMENT_TYPES.Extension,
        };
        const onSend = async (subject, body) => {
            await updateAgreementAsync({
                agreementId: agreement.id,
                data: requestBody,
            });
            await sendEmail({
                customers: formData.customerIds,
                subject,
                body,
            });
        };

        const recipientEmails = getCustomers(agreement)
            .map((customer) => customer.person?.email?.email_address)
            .filter((email) => email != null);

        openDialog({
            Component: SendEmailDialog,
            componentProps: {
                recipientEmails,
                onSendEmail: onSend,
            },
        });
    }, [form, agreement, updateAgreementAsync, sendEmail, openDialog]);

    const handleSubmitAnnexMode = useCallback(() => {
        const formData = form.getValues();
        const requestBody = {
            ...createRequestBodyFromFormData(formData),
            agreement_type: AGREEMENT_TYPES.Annex,
        };
        openDialog({
            Component: AddEditDocsRestrictedDialog,
            componentProps: {
                agreement_id: agreement.id,
                request_user: user?.username || "",
                multiple: true,
                showIndustry: true,
                availableDocsRestrictedTypes: [DOCS_RESTRICTED_USER, DOCS_RESTRICTED_ROLE],
                onSubmit: () => {
                    updateAgreement({ agreementId: agreement.id, data: requestBody });
                    closeDialog();
                },
            },
        });
    }, [form, agreement, updateAgreement, user, openDialog, closeDialog]);

    const handleSubmitTransformMode = useCallback(() => {
        const formData = form.getValues();
        const requestBody = {
            ...createRequestBodyFromFormData(formData),
            agreement_parent: parentAgreementId,
        };
        createAgreement(requestBody);
    }, [form, createAgreement, parentAgreementId]);

    const handleSubmit = useCallback(() => {
        switch (mode) {
            case AgreementConfigurationDialogMode.CREATE:
                handleSubmitCreateMode();
                break;
            case AgreementConfigurationDialogMode.EDIT:
                handleSubmitEditMode();
                break;
            case AgreementConfigurationDialogMode.PROLONGATE:
                handleSubmitProlongateMode();
                break;
            case AgreementConfigurationDialogMode.ANNEX:
                handleSubmitAnnexMode();
                break;
            case AgreementConfigurationDialogMode.TRANSFORM:
                handleSubmitTransformMode();
                break;
        }
    }, [
        handleSubmitCreateMode,
        handleSubmitEditMode,
        handleSubmitProlongateMode,
        handleSubmitAnnexMode,
        handleSubmitTransformMode,
        mode,
    ]);

    const fields = {
        agreementType: "agreementType",
        customerShares: "customerShares",
        caretakerId: "caretakerId",
        notarialDeedNumber: "notarialDeedNumber",
        notarialDeedSignDate: "notarialDeedSignDate",
        plannedAgreementSignDate: "plannedAgreementSignDate",
        agreementSignDate: "agreementSignDate",
        agreementReceptionDate: "agreementReceptionDate",
        agreementExpiryDate: "agreementExpiryDate",
        note: "note",
        productDiscounts: "productDiscounts",
        archiveNumber: "archiveNumber",
        agreementBrokerName: "agreementBrokerName",
        agreementBrokerPercentage: "agreementBrokerPercentage",
        payments: "payments",
        customerIds: "customerIds",
        isOperator: "agreementIsOperator",
        isWpk: "agreementIsWpk",
        isLoan: "agreementIsLoan",
        customerFlags: "customerFlags",
        agreement_banking: "agreement_banking",
        agreement_banking_withdrawal: "agreement_banking_withdrawal",
    };

    if (agreement == null && mode !== AgreementConfigurationDialogMode.CREATE) {
        throw new Error("Agreement must be provided in edit, annex, prolongate and transform mode");
    }

    return (
        <BasicDialog
            open={props.open}
            onClose={props.onClose}
            alwaysFullScreen={true}
            titleAlign={"center"}
            contentAlign={"center"}
            title={t("agreements.AgreementConfigurationDialog.title")}
            showDialogActions
        >
            <FormProvider {...form}>
                <form>
                    <AgreementDialogTabs
                        control={form.control}
                        fields={fields}
                        mode={mode}
                        selectedTab={selectedTab}
                        setSelectedTab={setSelectedTab}
                        parentAgreementKind={parentAgreementKind}
                        investmentId={agreement?.agreement_invest}
                        agreementHasRescheduledPayments={agreement?.has_rescheduled_payments}
                    />
                    <SubmitButton fields={fields} onSubmit={handleSubmit} loading={isPending} />
                </form>
            </FormProvider>
        </BasicDialog>
    );
};

const paymentCreateSchema = z.object({
    agrepay_milestone: z.string().uuid().optional().nullable(),
    agrepay_due_date: z.string().optional().nullable(),
    agrepay_plan_date: z.string().optional().nullable(),
    agrepay_kind: z.string().uuid(),
    agrepay_payment_shift: z.number().optional().nullable(),
    agrepay_notification_send_date: z.string().optional().nullable(),
    per_product_percentages: z.array(
        z.object({
            product_id: z.string().uuid(),
            percentage: z.number(),
        }),
    ),
});

export const createAgreementFormDataSchema = z.object({
    agreementType: z.nativeEnum(AgreementKindEnum),
    customerShares: z.array(
        z.object({
            customerIds: z.array(z.string().uuid()),
            percentage: z.number().int().min(0).max(100),
        }),
    ),
    caretakerId: z.string().uuid().optional(),
    notarialDeedNumber: z.string().optional(),
    notarialDeedSignDate: z.date().optional(),
    plannedAgreementSignDate: z.date().optional(),
    agreementSignDate: z.date().optional(),
    agreementReceptionDate: z.date().optional(),
    agreementExpiryDate: z.date().optional(),
    agreementIsOperator: z.boolean().optional(),
    agreementIsWpk: z.boolean().optional(),
    agreementIsLoan: z.boolean().optional(),
    agreement_banking: BankingSchema.optional().nullable(),
    agreement_banking_withdrawal: BankingSchema.optional().nullable(),
    note: z.string().optional(),
    productDiscounts: z.record(z.string().uuid(), z.number().min(0).max(100)),
    archiveNumber: z.string().optional(),
    agreementBrokerName: z.string().optional(),
    agreementBrokerPercentage: z.number().min(0).max(100).optional(),
    payments: z.array(paymentCreateSchema),
    customerIds: z.array(z.string().uuid()),
    customerFlags: z.record(
        z.string().uuid(),
        z.object({
            isForeigner: z.boolean(),
            isJdg: z.boolean(),
            isNip: z.boolean(),
        }),
    ),
});

const emptyFormData = {
    agreementType: DEFAULT_AGREEMENT_KIND,
    customerShares: [],
    caretakerId: undefined,
    notarialDeedNumber: undefined,
    notarialDeedSignDate: undefined,
    plannedAgreementSignDate: undefined,
    agreementSignDate: undefined,
    agreementReceptionDate: undefined,
    agreementExpiryDate: undefined,
    agreementIsOperator: false,
    agreementIsWpk: false,
    agreementIsLoan: false,
    note: undefined,
    productDiscounts: {},
    archiveNumber: undefined,
    agreementBrokerName: undefined,
    agreementBrokerPercentage: 0,
    payments: [],
    customerIds: [],
    customerFlags: {},
};

const formDataFromAgreement = (agreement, mode) => {
    const baseFormData = {
        agreementType: agreement.agreement_kind.agrekind_key,
        customerShares: agreement.shares.map((share) => ({
            customerIds: share.customers.map((c) => c.id),
            percentage: Number(share.percentage),
        })),
        caretakerId: agreement.agreement_seller?.id || undefined,
        productDiscounts: Object.fromEntries(
            agreement.products.map((product) => [
                product.product.id,
                Number(product.discount_proc),
            ]),
        ),
        payments: agreement.payments.map((payment) => ({
            agrepay_milestone: payment.agrepay_milestone ?? undefined,
            agrepay_due_date: payment.agrepay_due_date ?? undefined,
            agrepay_plan_date: payment.agrepay_plan_date ?? undefined,
            agrepay_kind: payment.agrepay_kind,
            agrepay_payment_shift: payment.agrepay_payment_shift ?? undefined,
            agrepay_notification_send_date: payment.agrepay_notification_send_date ?? undefined,
            per_product_percentages: payment.per_product_percentages.map((pp) => ({
                product_id: pp.product_id,
                percentage: Number(pp.percentage),
            })),
        })),
        customerIds: agreement.shares.flatMap((share) => share.customers.map((c) => c.id)),
        customerFlags: Object.fromEntries(
            agreement.customers.map((ac) => [
                ac.customer.id,
                {
                    isForeigner: ac.agrecust_is_foreigner,
                    isJdg: ac.agrecust_is_customer_jdg,
                    isNip: ac.agrecust_is_customer_nip,
                },
            ]),
        ),
    };

    const detailsFormData = {
        notarialDeedNumber: agreement.agreement_notarial_deed || undefined,
        notarialDeedSignDate: agreement.agreement_notarial_sign_date
            ? new Date(agreement.agreement_notarial_sign_date)
            : undefined,
        plannedAgreementSignDate: agreement.agreement_notarial_plan_date
            ? new Date(agreement.agreement_notarial_plan_date)
            : undefined,
        agreementReceptionDate: agreement.agreement_date_reception
            ? new Date(agreement.agreement_date_reception)
            : undefined,
        agreementSignDate: agreement.agreement_date_signed
            ? new Date(agreement.agreement_date_signed)
            : undefined,
        agreementExpiryDate: agreement.agreement_date_expires
            ? new Date(agreement.agreement_date_expires)
            : undefined,
        agreementIsOperator: agreement.agreement_is_operator || false,
        agreementIsWpk: agreement.agreement_is_wpk || false,
        agreementIsLoan: agreement.agreement_is_loan || false,
        agreement_banking: agreement.agreement_banking,
        agreement_banking_withdrawal: agreement.agreement_banking_withdrawal,
        archiveNumber: agreement.agreement_archive || undefined,
        agreementBrokerName: agreement.agreement_broker_name || undefined,
        agreementBrokerPercentage: agreement.agreement_broker_percentage || 0,
        note: agreement.agreement_note_sales || undefined,
    };

    if (mode === AgreementConfigurationDialogMode.TRANSFORM) {
        return baseFormData;
    }

    return {
        ...baseFormData,
        ...detailsFormData,
    };
};

const createRequestBodyFromFormData = (formData) => ({
    agreement_kind: AGREEMENT_KINDS.filter((k) => k.key === formData.agreementType.toString())[0]
        .id,

    agreement_type: AGREEMENT_TYPES.Agreement,

    shares: formData.customerShares.map((share) => ({
        customers: share.customerIds,
        percentage: share.percentage,
    })),

    agreement_seller: formData.caretakerId,
    agreement_notarial_deed: formData.notarialDeedNumber || "",
    agreement_notarial_sign_date: DateService.convertDateToFormatYYYYMMDD(
        formData.notarialDeedSignDate,
    ),
    agreement_notarial_plan_date: DateService.convertDateToFormatYYYYMMDD(
        formData.plannedAgreementSignDate,
    ),

    agreement_date_signed:
        DateService.convertDateToFormatYYYYMMDD(formData.agreementSignDate) || null,

    agreement_date_reception:
        DateService.convertDateToFormatYYYYMMDD(formData.agreementReceptionDate) || null,

    agreement_date_expires:
        DateService.convertDateToFormatYYYYMMDD(formData.agreementExpiryDate) || null,

    agreement_is_operator: formData.agreementIsOperator || false,
    agreement_is_wpk: formData.agreementIsWpk || false,
    agreement_is_loan: formData.agreementIsLoan || false,
    agreement_note_sales: formData.note || "",
    agreement_archive: formData.archiveNumber || "",
    agreement_broker_name: formData.agreementBrokerName || "",
    agreement_broker_percentage: formatDecimalNumber(formData.agreementBrokerPercentage, 10) || 0,
    agreement_banking: formData.agreement_banking?.id || null,
    agreement_banking_withdrawal: formData.agreement_banking_withdrawal?.id || null,

    products: Object.entries(formData.productDiscounts).map(([productId, discount]) => ({
        product: productId,
        discount_proc: normalizePercentageForSubmission(discount),
    })),

    payments: formData.payments.map((payment) => ({
        agrepay_milestone: payment.agrepay_milestone ?? null,
        agrepay_plan_date: payment.agrepay_plan_date
            ? DateService.convertDateToFormatYYYYMMDD(new Date(payment.agrepay_plan_date))
            : null,
        agrepay_due_date: payment.agrepay_due_date
            ? DateService.convertDateToFormatYYYYMMDD(new Date(payment.agrepay_due_date))
            : null,
        agrepay_kind: payment.agrepay_kind,
        agrepay_payment_shift: payment.agrepay_payment_shift ?? null,
        agrepay_notification_send_date: payment.agrepay_notification_send_date
            ? DateService.convertDateToFormatYYYYMMDD(
                  new Date(payment.agrepay_notification_send_date),
              )
            : null,
        agrepay_notification_draft: null,
        agrepay_transfer_is_paid: false,
        per_product_percentages: payment.per_product_percentages.map((ppp) => ({
            ...ppp,
            percentage: normalizePercentageForSubmission(ppp.percentage),
        })),
    })),

    customers: formData.customerIds.map((cid) => ({
        customer: cid,
        is_customer_nip: formData.customerFlags[cid]?.isNip || false,
        is_customer_jdg: formData.customerFlags[cid]?.isJdg || false,
        is_foreigner: formData.customerFlags[cid]?.isForeigner || false,
    })),
});

const SubmitButton = (props) => {
    const { fields, onSubmit, loading } = props;
    const { t } = useTranslation();
    const { isValid } = useIsAgreementFormDataValid(fields);

    return (
        <LoadingButton loading={loading} onClick={() => onSubmit()} disabled={!isValid}>
            {t("agreements.AgreementConfigurationDialog.submit")}
        </LoadingButton>
    );
};
